import { ApiService } from './api'
import type { CatalogSnapshotDto } from './catalog-model'

export * from './catalog-model'

export interface CatalogApiProblem {
	code: string
	message: string
}

export class CatalogApiError extends Error
{
	constructor(
		public readonly problem: CatalogApiProblem,
		public readonly status: number
	) {
		super(problem.message)
		this.name = 'CatalogApiError'
	}
}

const problemMessages: Record<string, string> = {
	catalog_unauthenticated: 'Войдите снова, чтобы открыть каталог.',
	catalog_space_forbidden: 'Это пространство недоступно.',
	catalog_space_invalid: 'Некорректный идентификатор пространства.',
	catalog_method_not_allowed: 'Каталог нельзя загрузить этим способом.',
	catalog_query_invalid: 'Параметры запроса каталога не поддерживаются.',
	catalog_not_ready: 'Глобальный каталог пока не готов.',
	catalog_read_failed: 'Не удалось загрузить глобальный каталог.',
}

export function normalizeCatalogApiError(error: unknown): { code: string; message: string; status: number }
{
	if (error instanceof CatalogApiError) {
		return {
			code: error.problem.code,
			message: problemMessages[error.problem.code] ?? error.problem.message,
			status: error.status,
		}
	}

	return {
		code: 'catalog_read_failed',
		message: problemMessages.catalog_read_failed,
		status: 0,
	}
}

export class CatalogService
{
	private readonly cpDomain: string

	constructor(private readonly apiService: ApiService)
	{
		this.cpDomain = apiService.getConfig().cp_domain
		if (!this.cpDomain)
			throw new Error('Отсутствует CP-домен в конфигурации сервера')
	}

	async load(spaceId: string | number): Promise<CatalogSnapshotDto>
	{
		const response = await this.apiService.doFetch(
			this.cpDomain,
			`/${spaceId}/settings/s/catalog`,
			'GET'
		)
		const result = await this.apiService.getResponseJson(response)
		if (!result.success || !result.data) {
			const rawProblem = result.data?.problem
			throw new CatalogApiError({
				code: typeof rawProblem?.code === 'string' ? rawProblem.code : 'catalog_read_failed',
				message: typeof rawProblem?.message === 'string'
					? rawProblem.message
					: (result.error || 'Could not read the catalog.'),
			}, result.response_code || response.status)
		}

		return result.data as CatalogSnapshotDto
	}
}
